import type { codec } from "@irriga-mais/proto";
import type Long from "long";
import type { LICStateProperties, LicState } from "@/api/model/lic-state";
import type { AUTProject, AUTProperty, AUTReservoir, AUTSector, AUTWaterPump } from "@/api/queries/account";

export type DeviceState = {
  synced: boolean;
  failed: boolean;
  outputOn: boolean;
  inputOn: boolean;
};

export function getEquipamentState(
  equipamentId: string,
  status: codec.out.status.ISystemStatusPackage,
  licState: LicState
): DeviceState | null {
  const devices = licState.devices as LICStateProperties["devices"];
  const device = devices.find((d) => d.elementId === equipamentId);
  if (!device) {
    return null;
  }
  return getDeviceState(device.ord_idx, status);
}

export function getDeviceState(deviceOrdIdx: number, status: codec.out.status.ISystemStatusPackage): DeviceState {
  return {
    synced: checkBitmask(status.sync_bitmask, deviceOrdIdx),
    failed: checkBitmask(status.failed_bitmask, deviceOrdIdx),
    outputOn: checkBitmask(status.on_bitmask, deviceOrdIdx),
    inputOn: checkBitmask(status.input_bitmask, deviceOrdIdx),
  };
}

function checkBitmask(bitmask: number | bigint | string | undefined | null | Long, bitIndex: number): boolean {
  if (bitmask === undefined || bitmask === null) {
    return false;
  }

  try {
    // Convert to BigInt to handle large bitmasks safely
    const mask = BigInt(bitmask.toString());
    const bit = BigInt(1) << BigInt(bitIndex);
    return (mask & bit) !== BigInt(0);
  } catch (error) {
    console.error(`Error checking bitmask ${bitmask} at index ${bitIndex}:`, error);
    return false;
  }
}

export function getIrrigationPlanState(
  id: string,
  licState: LicState,
  schedulingReportPackage: codec.out.scheduling_report.ISchedulingReportPackage
) {
  const schedules = licState.schedules as LICStateProperties["schedules"];
  const schedule = schedules.find((s) => s.irrigationPlanId === id);
  if (!schedule) {
    return null;
  }
  const state = schedulingReportPackage.data?.find((s) => s.scheduling_idx === schedule.ord_idx);
  return state || null;
}

export type LicVirtualDeviceEquipment = {
  virtualDevice: LICStateProperties["devices"][number];
} & (
  | {
      elementType: "water_pump";
      element: AUTWaterPump;
    }
  | {
      elementType: "reservoir";
      element: AUTReservoir;
    }
  | {
      elementType: "sector";
      element: AUTSector;
    }
  | {
      elementType: "tank_ferti";
      element: AUTProject;
    }
);

type WithoutVirtualDevice<T> = T extends unknown ? Omit<T, "virtualDevice"> : never;

export type LicVirtualDeviceEquipmentDetails = WithoutVirtualDevice<LicVirtualDeviceEquipment>;

export function findEquipmentIdByOrdIdx(
  ordIdx: number,
  licState: LicState,
  property: AUTProperty
): LicVirtualDeviceEquipment | null {
  const devices = licState.devices as LICStateProperties["devices"];
  const device = devices.find((d) => d.ord_idx === ordIdx);
  if (!device?.elementId) {
    return null;
  }

  let element: AUTWaterPump | AUTReservoir | AUTSector | undefined = property.water_pumps.find(
    (p) => p.id === device.elementId
  );
  if (element) {
    return {
      elementType: "water_pump",
      element: element,
      virtualDevice: device,
    };
  }

  element = property.reservoirs.find((r) => r.id === device.elementId);
  if (element) {
    return {
      elementType: "reservoir",
      element: element,
      virtualDevice: device,
    };
  }

  for (const project of property.projects) {
    if (device.elementVariation === "tank-ferti" && project.id === device.elementId) {
      return { elementType: "tank_ferti", element: project, virtualDevice: device };
    }

    element = project.sectors.find((s) => s.id === device.elementId);
    if (element) {
      return { elementType: "sector", element: element, virtualDevice: device };
    }
  }

  return null;
}
